import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function CandidateForm({ jobId, jobTitle, compact = false, onSuccess }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    linkedin_url: "",
    salary_expectation: "",
    notice_period: "",
    current_role: "",
    message: "",
  });
  const [cv, setCv] = useState(null);
  const [gdpr, setGdpr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!gdpr) {
      toast.error("Please confirm GDPR consent to continue.");
      return;
    }
    if (cv && cv.size > 10 * 1024 * 1024) {
      toast.error("CV exceeds 10MB limit.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("gdpr_consent", "true");
      if (jobId) fd.append("job_id", jobId);
      if (jobTitle) fd.append("job_title", jobTitle);
      if (cv) fd.append("cv", cv);

      await api.post("/candidates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccess(true);
      toast.success("Application received. Owen will be in touch personally.");
      setTimeout(() => {
        onSuccess?.();
      }, 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="candidate-success" className="text-center py-10">
        <CheckCircle2 size={48} strokeWidth={1.25} className="text-[#C9A876] mx-auto mb-5" />
        <h3 className="font-display text-2xl text-white mb-2">Application received.</h3>
        <p className="text-slate-400 text-sm">
          Your details have been logged confidentially. Owen will reach out personally
          if there is alignment.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full bg-white/5 border border-white/10 text-white placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-[#C9A876] focus:ring-1 focus:ring-[#C9A876] transition-colors";

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="candidate-form">
      <div className={`grid ${compact ? "grid-cols-1 md:grid-cols-2" : "md:grid-cols-2"} gap-4`}>
        <Field label="Full name *">
          <input
            data-testid="cand-name"
            required
            className={inputCls}
            value={form.full_name}
            onChange={change("full_name")}
          />
        </Field>
        <Field label="Email *">
          <input
            data-testid="cand-email"
            required
            type="email"
            className={inputCls}
            value={form.email}
            onChange={change("email")}
          />
        </Field>
        <Field label="LinkedIn URL *">
          <input
            data-testid="cand-linkedin"
            required
            type="url"
            placeholder="https://linkedin.com/in/…"
            className={inputCls}
            value={form.linkedin_url}
            onChange={change("linkedin_url")}
          />
        </Field>
        <Field label="Phone (optional)">
          <input
            data-testid="cand-phone"
            className={inputCls}
            value={form.phone}
            onChange={change("phone")}
          />
        </Field>
        <Field label="Salary expectation *">
          <input
            data-testid="cand-salary"
            required
            placeholder="e.g. £180k base + equity"
            className={inputCls}
            value={form.salary_expectation}
            onChange={change("salary_expectation")}
          />
        </Field>
        <Field label="Notice period *">
          <input
            data-testid="cand-notice"
            required
            placeholder="e.g. 3 months"
            className={inputCls}
            value={form.notice_period}
            onChange={change("notice_period")}
          />
        </Field>
        <Field label="Current role / Company" full={compact}>
          <input
            data-testid="cand-current-role"
            className={inputCls}
            value={form.current_role}
            onChange={change("current_role")}
          />
        </Field>
      </div>

      <Field label="Message (optional)">
        <textarea
          data-testid="cand-message"
          rows={3}
          className={`${inputCls} resize-none`}
          placeholder="Anything Owen should know about your search…"
          value={form.message}
          onChange={change("message")}
        />
      </Field>

      <Field label="CV (PDF, DOC, DOCX · max 10MB)">
        <label
          data-testid="cand-cv-label"
          className="flex items-center gap-3 cursor-pointer border border-dashed border-white/15 hover:border-[#C9A876]/60 px-4 py-4 text-sm text-slate-300 transition-colors"
        >
          <Upload size={18} strokeWidth={1.5} className="text-[#C9A876]" />
          <span className="truncate flex-1">
            {cv ? cv.name : "Upload your CV"}
          </span>
          <input
            data-testid="cand-cv-input"
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setCv(e.target.files?.[0] || null)}
            className="hidden"
          />
        </label>
      </Field>

      <label className="flex items-start gap-3 text-xs text-slate-400 leading-relaxed cursor-pointer">
        <input
          data-testid="cand-gdpr"
          type="checkbox"
          checked={gdpr}
          onChange={(e) => setGdpr(e.target.checked)}
          className="mt-0.5 accent-[#C9A876] w-4 h-4"
        />
        <span>
          I consent to Arcpoint storing and processing my information for
          confidential leadership opportunities, in accordance with UK GDPR.
          You may withdraw consent at any time.
        </span>
      </label>

      <motion.button
        whileTap={{ scale: 0.985 }}
        type="submit"
        disabled={submitting}
        data-testid="cand-submit"
        className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#C9A876] hover:bg-[#DCC298] disabled:opacity-60 disabled:cursor-not-allowed text-black text-sm font-semibold tracking-wide transition-colors duration-300"
      >
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? "Submitting…" : "Submit Confidentially"}
      </motion.button>
    </form>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="font-mono-eyebrow text-slate-400 mb-2 block">{label}</span>
      {children}
    </label>
  );
}
